import { CSSProperties, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { KaziAtlasLogo } from '../components/KaziAtlasLogo';
import { useTheme } from '../hooks/useTheme';
import { usePageTitle } from '../hooks/usePageTitle';

/**
 * PublicTaskPage — read-only view behind a task's "Public Link"
 * (TaskDetailPage). The link lives on the SPA origin, but the data is served
 * unauthenticated by the API host at GET /public/tasks/{slug} (only tasks with
 * is_public = true). Shows only what a recipient needs; internal ids in the
 * response are never rendered.
 */

const API_BASE = import.meta.env.VITE_API_URL || '';

interface PublicTask {
    title: string;
    description?: string;
    status: string;
    priority: string;
    start_date?: string;
    due_date?: string;
}

type State =
    | { kind: 'loading' }
    | { kind: 'ok'; task: PublicTask }
    | { kind: 'missing' }
    | { kind: 'error' };

const LABELS: Record<string, string> = {
    todo: 'To do', in_progress: 'In progress', blocked: 'Blocked', review: 'In review', done: 'Done',
    none: 'None', low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent',
};
const label = (v: string) => LABELS[v] ?? v;
const day = (v?: string) => (v ? v.slice(0, 10) : undefined);

const INK = 'var(--ink)';
const MUTED = 'var(--ink-muted)';
const BORDER = '1px solid var(--border)';

const s: Record<string, CSSProperties> = {
    page: { background: 'var(--bg)', color: 'var(--ink-body)', fontFamily: 'var(--font-body)', minHeight: '100vh', display: 'flex', flexDirection: 'column' },
    nav: { borderBottom: BORDER, background: 'var(--surface)' },
    navInner: { maxWidth: 820, margin: '0 auto', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem' },
    navHome: { background: 'none', border: 'none', color: MUTED, fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', padding: 0 },
    main: { maxWidth: 820, margin: '0 auto', width: '100%', padding: '3rem 1rem 5rem', flex: 1 },
    h1: { fontSize: 'clamp(1.75rem, 4vw, 2.25rem)', fontWeight: 800, lineHeight: 1.2, color: INK, margin: '0 0 1rem' },
    meta: { display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1.5rem', margin: '0 0 2rem', padding: 0, listStyle: 'none', color: MUTED, fontSize: '0.875rem' },
    metaValue: { color: INK, fontWeight: 600 },
    desc: { whiteSpace: 'pre-wrap', lineHeight: 1.75, margin: 0 },
    p: { lineHeight: 1.75, margin: '0 0 1rem' },
};

export function PublicTaskPage() {
    const { slug = '' } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const [state, setState] = useState<State>({ kind: 'loading' });
    usePageTitle(state.kind === 'ok' ? state.task.title : 'Shared task');

    useEffect(() => {
        let cancelled = false;
        setState({ kind: 'loading' });
        fetch(`${API_BASE}/public/tasks/${encodeURIComponent(slug)}`, { credentials: 'omit' })
            .then(async (res) => {
                if (res.status === 404) return { kind: 'missing' } as const;
                if (!res.ok) return { kind: 'error' } as const;
                return { kind: 'ok', task: (await res.json()) as PublicTask } as const;
            })
            .catch(() => ({ kind: 'error' }) as const)
            .then((next) => { if (!cancelled) setState(next); });
        return () => { cancelled = true; };
    }, [slug]);

    return (
        <div style={s.page}>
            <nav style={s.nav}>
                <div style={s.navInner}>
                    <KaziAtlasLogo size={28} onLight={theme === 'light'} />
                    <button style={s.navHome} onClick={() => navigate('/')}>← KaziAtlas home</button>
                </div>
            </nav>
            <main style={s.main}>
                {state.kind === 'loading' && <p style={s.p} aria-busy="true">Loading task…</p>}
                {state.kind === 'missing' && (
                    <>
                        <h1 style={s.h1}>This task isn't available</h1>
                        <p style={s.p}>The link may be wrong, or the task is no longer shared publicly.</p>
                    </>
                )}
                {state.kind === 'error' && (
                    <p style={s.p} role="alert">We couldn't load this task. Check your connection and refresh the page.</p>
                )}
                {state.kind === 'ok' && (
                    <article>
                        <h1 style={s.h1}>{state.task.title}</h1>
                        <ul style={s.meta}>
                            <li>Status <span style={s.metaValue}>{label(state.task.status)}</span></li>
                            <li>Priority <span style={s.metaValue}>{label(state.task.priority)}</span></li>
                            {day(state.task.start_date) && <li>Start <span style={s.metaValue}>{day(state.task.start_date)}</span></li>}
                            {day(state.task.due_date) && <li>Due <span style={s.metaValue}>{day(state.task.due_date)}</span></li>}
                        </ul>
                        {state.task.description
                            ? <p style={s.desc}>{state.task.description}</p>
                            : <p style={{ ...s.p, color: MUTED }}>No description.</p>}
                    </article>
                )}
            </main>
        </div>
    );
}
